'use client';
import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    id: 'faq-accuracy',
    q: 'How accurate are the AI-generated visa requirements?',
    a: 'VisaPilot AI pulls requirements from a continuously updated database of embassy rules, IATA Travel Centre data, and official government sources. Our database is reviewed weekly by visa specialists. That said, always verify final requirements directly with the embassy before submission — visa rules can change without notice.',
  },
  {
    id: 'faq-rejection',
    q: 'What happens if my visa is rejected after using VisaPilot?',
    a: 'VisaPilot generates your application package based on the information you provide and known embassy requirements. We cannot guarantee approval as the final decision rests with the embassy. However, our 98.2% approval rate reflects the quality of packages our users submit. If rejected, we offer a free re-evaluation of your package.',
  },
  {
    id: 'faq-languages',
    q: 'Which languages are supported for document generation?',
    a: 'Currently supported: English, Arabic (Modern Standard), Urdu, and Hindi. Your entire package — cover letter, itinerary, sponsor letter, and checklist — can be generated in any of these languages. Hindi is in active development and may have limited coverage for some visa types.',
  },
  {
    id: 'faq-security',
    q: 'How are my passport and financial documents secured?',
    a: 'All uploaded documents are encrypted using AES-256 at rest and TLS 1.3 in transit. Sensitive fields (passport number, financial figures) are automatically blurred in UI previews. We never share your documents with third parties. You can request permanent deletion of all data at any time from your account settings.',
  },
  {
    id: 'faq-agency',
    q: 'Can I use VisaPilot for my visa agency or consultancy?',
    a: 'Yes — our Agency plans are designed specifically for travel agencies and visa consultants. You get a dedicated client management dashboard, token-based billing (so you only pay for what you process), white-label PDF export, and API access on the Plus plan. Start with Agency Basic at $299/month.',
  },
  {
    id: 'faq-countries',
    q: 'Which countries and visa types are supported?',
    a: 'VisaPilot covers 180+ destination countries across all major visa categories: Tourist, Student, Work, Business, Transit, Family Reunion, and Medical. Coverage varies by country — some destinations have full AI document generation, others provide checklist and eligibility guidance only. Check our coverage map for your specific destination.',
  },
  {
    id: 'faq-refund',
    q: 'Can I get a refund if I change my mind?',
    a: 'For individual one-time payments, we offer a full refund within 24 hours of purchase if you have not downloaded your package. Once the PDF bundle is downloaded, refunds are not available. Agency subscriptions can be cancelled anytime; you retain access until the end of your billing period.',
  },
  {
    id: 'faq-upload',
    q: 'What document formats can I upload?',
    a: 'We accept PDF, JPEG, PNG, and HEIC formats. Maximum file size is 25MB per document. Our AI validator works best with clear, high-resolution scans. Blurry or low-contrast uploads will be flagged with specific guidance on how to re-scan them.',
  },
];

export default function FaqSection() {
  const [openId, setOpenId] = useState<string | null>('faq-accuracy');

  return (
    <section className="py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Left: Header */}
          <div className="space-y-4">
            <p className="section-label">FAQ</p>
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight leading-snug">
              Questions we get asked most
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              Can&apos;t find what you&apos;re looking for? Email us at{' '}
              <a href="mailto:support@visapilot.ai" className="text-blue-600 font-semibold hover:underline">
                support@visapilot.ai
              </a>
            </p>
            <div className="pt-2 space-y-2">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Popular topics</div>
              {['Visa requirements accuracy', 'Document security', 'Agency billing', 'Language support']?.map((topic) => (
                <div key={`topic-${topic}`} className="text-sm text-blue-600 font-medium hover:underline cursor-pointer">
                  → {topic}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Accordion */}
          <div className="lg:col-span-2 space-y-2">
            {faqs?.map((faq) => (
              <div
                key={faq?.id}
                className={`border rounded-xl overflow-hidden transition-all duration-200 ${
                  openId === faq?.id ? 'border-blue-200 bg-blue-50/40' : 'border-gray-100 bg-white hover:border-gray-200'
                }`}
              >
                <button
                  onClick={() => setOpenId(openId === faq?.id ? null : faq?.id)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="text-sm font-semibold text-gray-900">{faq?.q}</span>
                  <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-200 ${
                    openId === faq?.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {openId === faq?.id ? <Minus size={12} /> : <Plus size={12} />}
                  </div>
                </button>
                {openId === faq?.id && (
                  <div className="px-5 pb-4 animate-fade-in">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}